import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fileToBase64 } from "../../../utils/fileUtils";
import {
  mapMessageAttachments,
  fetchChatAttachmentsApi,
  getValidNocType,
} from "../../../utils/nocChatUtils";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

export interface ChatMessage {
  id: number;
  nocRequestId: number;
  senderEmpId: string;
  senderName: string;
  senderType: "employee" | "admin" | "approver";
  message: string;
  attachments?: any[];
  isRead: boolean;
  createdAt: string;
  documentType?: string; // 'Education' | 'Advertisement' | 'Others'
  senderImage?: string;
  documents?: any[];
}

interface ChatState {
  messages: ChatMessage[];
  attachments: any[];
  loading: boolean;
  error: string | null;
  unreadCount: number;
  uploading: boolean;
  uploadError: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const initialState: ChatState = {
  messages: [],
  attachments: [],
  loading: false,
  error: null,
  unreadCount: 0,
  uploading: false,
  uploadError: null,
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  },
};

// Async thunks
export const uploadChatFiles = createAsyncThunk(
  "nocChat/uploadFiles",
  async (files: File[], { rejectWithValue }) => {
    try {
      const uploadPromises = files.map(async (file) => {
        const rawBase64 = await fileToBase64(file);
        const filestring = rawBase64.includes(",")
          ? rawBase64.split(",")[1]
          : rawBase64;
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        const mime_type =
          file.type && /^[a-z0-9\-_.]+\/[a-z0-9\-_.]+$/i.test(file.type)
            ? file.type
            : ext === "pdf"
              ? "application/pdf"
              : ext === "png"
                ? "image/png"
                : ext === "jpg" || ext === "jpeg"
                  ? "image/jpeg"
                  : ext === "doc"
                    ? "application/msword"
                    : ext === "docx"
                      ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      : "application/octet-stream";

        const response: any = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, {
          filestring,
          mime_type,
          module: "chat",
        });

        const resData = response?.data?.data || response?.data || response;
        const key = resData?.key;
        const url = resData?.url;

        if (!key) {
          throw new Error(
            response?.message ||
            response?.data?.message ||
            `Failed to upload ${file.name}`
          );
        }

        return {
          key,
          serverKey: key,
          fileName: file.name,
          url,
          serverURL: url,
          mime_type,
          fileType: mime_type,
          fileSize: `${Math.round(file.size / 1024)}KB`,
        };
      });

      const uploadResults = await Promise.all(uploadPromises);
      return uploadResults;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const sendMessage = createAsyncThunk(
  "nocChat/sendMessage",
  async (
    {
      nocRequestId,
      message,
      attachments,
      documents,
      documentType,
    }: {
      nocRequestId: number;
      message: string;
      attachments?: any[];
      documents?: any[];
      documentType?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const docs = attachments || documents || [];
      const payload: any = {
        nocRequestId,
        message,
        attachments: docs,
      };
      if (documentType || (docs.length > 0 && docs[0]?.documentType)) {
        payload.documentType = documentType || docs[0]?.documentType || "Upload Documents";
      }

      const response: any = await Api.post(API_URLS.NOC.SEND_CHAT_MESSAGE, payload);

      if (
        (response && response.success === false) ||
        (response?.status && response.status >= 400) ||
        (response?.statusCode && response.statusCode >= 400) ||
        (response?.data?.statusCode && response.data.statusCode >= 400)
      ) {
        throw new Error(
          response?.message ||
          response?.data?.message ||
          "Failed to send message"
        );
      }

      return response;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const fetchChatAttachments = createAsyncThunk(
  "nocChat/fetchAttachments",
  async (
    { nocRequestId, nocType }: { nocRequestId: number; nocType: string },
    { rejectWithValue }
  ) => {
    try {
      const attachments = await fetchChatAttachmentsApi(nocRequestId, nocType);
      return attachments;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const fetchMessages = createAsyncThunk(
  "nocChat/fetchMessages",
  async (
    { nocRequestId, page = 1 }: { nocRequestId: number; page?: number },
    { rejectWithValue }
  ) => {
    try {
      console.log("[fetchMessages] Calling API with:", { nocRequestId, page });
      console.log("[fetchMessages] API URL:", API_URLS.NOC.GET_CHAT_MESSAGES);

      const response = await Api.post(API_URLS.NOC.GET_CHAT_MESSAGES, {
        nocRequestId,
        page,
      });

      console.log("[fetchMessages] Response:", response);

      if (!response.data || response.data.statusCode >= 400) {
        console.error("[fetchMessages] Error response:", response.data);
        throw new Error(response.data?.message || "Failed to fetch messages");
      }

      return response;
    } catch (error: any) {
      console.error("[fetchMessages] Error fetching messages:", error);
      console.error("[fetchMessages] Error details:", {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "nocChat/fetchUnreadCount",
  async (nocRequestId: number, { rejectWithValue }) => {
    try {
      console.log(
        "[fetchUnreadCount] Calling API with nocRequestId:",
        nocRequestId
      );
      console.log(
        "[fetchUnreadCount] API URL:",
        `${API_URLS.NOC.GET_UNREAD_COUNT}/${nocRequestId}`
      );

      const response = await Api.post(
        `${API_URLS.NOC.GET_UNREAD_COUNT}/${nocRequestId}`,
        {} // Send empty object instead of null
      );

      console.log("[fetchUnreadCount] Response:", response);

      if (!response.data || response.data.statusCode >= 400) {
        console.error("[fetchUnreadCount] Error response:", response.data);
        throw new Error(
          response.data?.message || "Failed to fetch unread count"
        );
      }

      return response;
    } catch (error: any) {
      console.error("[fetchUnreadCount] Error:", error);
      console.error("[fetchUnreadCount] Error details:", {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const nocChatSlice = createSlice({
  name: "nocChat",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.messages = [];
      state.unreadCount = 0;
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    clearUploadError: (state) => {
      state.uploadError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Send message
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        const resData =
          action.payload?.data?.data ||
          action.payload?.data ||
          action.payload;
        const nocChatId =
          resData?.nocChatId ||
          resData?.id ||
          action.payload?.data?.nocChatId ||
          Date.now();

        const sentAttachments =
          action.meta.arg?.attachments || action.meta.arg?.documents || [];
        const sentMessage = action.meta.arg?.message || "";
        const sentDocType = action.meta.arg?.documentType;

        const optimisticMsg: any = {
          id: nocChatId,
          nocChatId: nocChatId,
          nocRequestId: action.meta.arg?.nocRequestId,
          senderEmpId: "",
          senderName: "You",
          senderType: "employee",
          message: sentMessage,
          attachments: sentAttachments,
          isRead: true,
          createdAt: new Date().toISOString(),
          documentType: sentDocType,
        };

        const existingIdx = state.messages.findIndex(
          (m: any) =>
            m.id === nocChatId ||
            m.nocChatId === nocChatId ||
            (m.message && m.message.trim() === sentMessage.trim())
        );

        if (existingIdx !== -1) {
          state.messages[existingIdx] = {
            ...state.messages[existingIdx],
            ...optimisticMsg,
            attachments: sentAttachments.length > 0 ? sentAttachments : state.messages[existingIdx].attachments,
          };
        } else {
          state.messages.push(optimisticMsg);
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to send message";
      })

      // Fetch messages
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        const resData = action.payload?.data;
        const rawMessages =
          resData?.data?.messages ||
          resData?.messages ||
          (Array.isArray(resData?.data) ? resData.data : null) ||
          (Array.isArray(resData) ? resData : null) ||
          action.payload?.messages ||
          [];
        const messages = Array.isArray(rawMessages)
          ? rawMessages.map((m: any) => mapMessageAttachments(m, state.attachments))
          : [];

        // Preserve direct URLs/keys from recently sent attachments if server returns empty documents
        state.messages = messages.map((m: any) => {
          const existing = state.messages.find(
            (prev: any) =>
              prev.id === m.id ||
              (m.id && prev.nocChatId === m.id) ||
              (prev.id && m.nocChatId === prev.id) ||
              (prev.message && m.message && prev.message.trim() === m.message.trim()) ||
              (prev.message && m.message && (
                prev.message.includes(m.message.trim()) ||
                m.message.includes(prev.message.trim())
              ))
          );
          if (
            existing &&
            existing.attachments &&
            existing.attachments.length > 0 &&
            (!m.attachments || m.attachments.length === 0 || !m.attachments[0]?.url)
          ) {
            return {
              ...m,
              attachments: existing.attachments,
            };
          }
          return m;
        });
        state.pagination =
          resData?.data?.pagination || resData?.pagination || state.pagination;
      })
      // Fetch attachments
      .addCase(fetchChatAttachments.fulfilled, (state, action) => {
        state.attachments = Array.isArray(action.payload) ? action.payload : [];
        if (state.attachments.length > 0 && state.messages.length > 0) {
          state.messages = state.messages.map((m: any) =>
            mapMessageAttachments(m, state.attachments)
          );
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch messages";
      })

      // Fetch unread count
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        if (action.payload.data && action.payload.data.data) {
          state.unreadCount = action.payload.data.data.count || 0;
        }
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        console.error("Failed to fetch unread count:", action.payload);
      })

      // Upload files
      .addCase(uploadChatFiles.pending, (state) => {
        state.uploading = true;
        state.uploadError = null;
      })
      .addCase(uploadChatFiles.fulfilled, (state) => {
        state.uploading = false;
      })
      .addCase(uploadChatFiles.rejected, (state, action) => {
        state.uploading = false;
        state.uploadError =
          (action.payload as string) || "Failed to upload files";
      });
  },
});

export const { clearMessages, addMessage, clearError, clearUploadError } =
  nocChatSlice.actions;
export default nocChatSlice.reducer;
